import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { DesignSystemTheme } from '@minecraft.themes';
import type { MediaPhotoSource } from './types';

const FALLBACK_SRC_SVG =
  'data:image/svg+xml,%3Csvg viewBox="0 0 16 9" xmlns="http://www.w3.org/2000/svg"%3E%3Crect width="16" height="9" fill="black"/%3E%3C/svg%3E';

export type MediaPhotoFluidComponentProps = {
  /** Available image URLs with their intrinsic dimensions. */
  sources: MediaPhotoSource[];
  /** Alt text for the image for accessibility. */
  alt: string;
  /** Sets the `data-qa-id` attribute (Playwright selector). */
  'data-qa-id'?: string;
  /** Sets the `data-testid` attribute (Jest selector). */
  'data-testid'?: string;
  /** Maximum rendered height for the image and its wrapper. */
  maxHeight?: React.CSSProperties['maxHeight'];
  /** Background color shown around images that do not fill the available area. */
  bgColor?: keyof DesignSystemTheme['bgColors'];
  /** Fallback image to display when there are no sources or the image fails to load. */
  fallbackSrc?: string;
};

/**
 * Determines the fit tier of a source relative to the container dimensions.
 * Tier 2: Both width and height meet or exceed the container's dimensions.
 * Tier 1: Only the width meets or exceeds the container's width.
 * Tier 0: Neither dimension meets the container's requirements.
 */
const getFitTier = (source: MediaPhotoSource, widthParam: number, heightParam: number) => {
  if (source.width >= widthParam && source.height >= heightParam) return 2;
  return source.width >= widthParam ? 1 : 0;
};

const findBestSource = (
  sources: MediaPhotoSource[],
  widthParam: number,
  heightParam: number
): MediaPhotoSource | undefined => {
  /** validate that sources is a non-empty array of objects with finite width and height */
  if (!Array.isArray(sources)) {
    return undefined;
  }
  const validSources = sources.filter(
    (source) => Number.isFinite(source.width) && source.width > 0 && Number.isFinite(source.height) && source.height > 0
  );
  if (validSources.length === 0) return undefined;

  /** if we do not have width & height for the container, select the smallest valid source */
  if (!Number.isFinite(widthParam) || widthParam <= 0 || !Number.isFinite(heightParam) || heightParam <= 0) {
    return validSources.reduce(
      (smallest, source) => (source.width * source.height < smallest.width * smallest.height ? source : smallest),
      validSources[0]
    );
  }

  /** select the best fitting source based on the container dimensions
   * the specific selection logic is as follows:
   * - Prefer sources that fully meet or exceed the container's dimensions (Tier 2).
   * - If no Tier 2 sources are available, prefer sources that at least meet the container's width (Tier 1).
   * - If no Tier 1 sources are available, select the largest available source (Tier 0).
   */
  return validSources.reduce<MediaPhotoSource>((best, source) => {
    const sourceFitTier = getFitTier(source, widthParam, heightParam);
    const bestFitTier = getFitTier(best, widthParam, heightParam);
    if (sourceFitTier !== bestFitTier) return sourceFitTier > bestFitTier ? source : best;

    if (sourceFitTier === 0) {
      return source.width > best.width || (source.width === best.width && source.height > best.height) ? source : best;
    }

    return source.width * source.height < best.width * best.height ? source : best;
  }, validSources[0]);
};

/** MediaPhotoFluid works responsively within its parent's bounded area to render the photo with the best available resolution.
 * it keeps the image's aspect ratio intact while filling the available space as best as possible.
 *
 * Use this for a responsive image that adapts to its container's size where the size of the image is not known in advance.
 *
 * The component will fill the available space within its parent container while maintaining the image's aspect ratio and preventing distortion.
 * The parent container should define specific width and height CSS values to ensure the image is displayed correctly.
 */
export const MediaPhotoFluid = ({
  alt,
  sources,
  'data-qa-id': qaId,
  'data-testid': testId,
  maxHeight,
  bgColor,
  fallbackSrc,
}: MediaPhotoFluidComponentProps) => {
  const [failedSrcs, setFailedSrcs] = useState<Set<string>>(() => new Set());
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const source = findBestSource(
    Array.isArray(sources) ? sources.filter((candidate) => !failedSrcs.has(candidate.src)) : [],
    containerSize.width,
    containerSize.height
  );
  const errorSrc = fallbackSrc ?? FALLBACK_SRC_SVG;
  const imageMaxHeight = maxHeight ?? '100%';

  // Keep the measured container size in sync so source selection can respond to layout changes.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateContainerSize = (width: number, height: number) => {
      setContainerSize((currentSize) => {
        // Reuse the existing state when nothing changed to avoid unnecessary renders.
        if (currentSize.width === width && currentSize.height === height) {
          return currentSize;
        }

        return { width, height };
      });
    };

    // useLayoutEffect lets us measure and select a source before the browser paints.
    const initialBounds = container.getBoundingClientRect();
    updateContainerSize(initialBounds.width, initialBounds.height);

    if (typeof ResizeObserver === 'undefined') return;

    let pendingResizeFrameId: number | undefined;
    const resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;

      // Several notifications can arrive before the next frame. Keep only the latest size.
      if (pendingResizeFrameId !== undefined) {
        cancelAnimationFrame(pendingResizeFrameId);
      }

      // Changing the image source can resize this container again. Defer the state update
      // to the next frame so it cannot trigger a resize loop within this notification cycle.
      pendingResizeFrameId = requestAnimationFrame(() => {
        pendingResizeFrameId = undefined;
        updateContainerSize(width, height);
      });
    });
    resizeObserver.observe(container);

    // eslint-disable-next-line consistent-return -- observer cleanup is conditional on the container existing.
    return () => {
      // Stop notifications and pending updates when unmounting or restarting the effect.
      resizeObserver.disconnect();
      if (pendingResizeFrameId !== undefined) {
        cancelAnimationFrame(pendingResizeFrameId);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: 'auto',
        maxWidth: '100%',
        maxHeight: imageMaxHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        alt={alt}
        src={source?.src ?? errorSrc}
        style={{
          display: 'block',
          width: 'auto',
          height: 'auto',
          maxWidth: '100%',
          maxHeight: imageMaxHeight,
          objectFit: 'contain',
          backgroundColor: theme.designSystem.bgColors[bgColor ?? 'card'],
        }}
        {...(qaId ? { 'data-qa-id': qaId } : {})}
        {...(testId ? { 'data-testid': testId } : {})}
        onError={() => {
          if (!source) return;
          setFailedSrcs((currentFailedSrcs) => {
            if (currentFailedSrcs.has(source.src)) return currentFailedSrcs;
            return new Set(currentFailedSrcs).add(source.src);
          });
        }}
      />
    </div>
  );
};

MediaPhotoFluid.displayName = 'MediaPhotoFluid';
